import { AxiosInstance } from 'axios'
import { ICreateOrganization, ITokensAvailable } from '../models/Organization'
import { IBusiness } from '../models/Business'
import { BaseHTTP } from './base.http'
import { CreateComprobantesDto } from '../models/Comprobante'
import { UpdateLibroContableDto } from '../models/LibroContable'
import { UpdateMultiLibros } from '../models/MultiLibros'

const pendingUnlinkRequests = new Map<string, Promise<any>>()

export class OrganizationAPI {
  private http!: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  createOrganization(payload: ICreateOrganization) {
    const path = 'v1/organizations'
    return this.http.post<{ token: string }>(path, payload)
  }

  verification(payload: { uid: string; email: string }) {
    const path = 'v1/organizations'
    return this.http.patch<void>(path, payload)
  }

  // obtener datos de un ingreso / egreso en particular desde la tabla
  getDataLibroContable(businessId, comprobanteId, type) {
    const path = `v1/business/${businessId}/libros-contables/${comprobanteId}?type=${type}&centrocosto=${true}`
    return this.http.get(path)
  }

  // editar  libro contable de una organización
  patchDataLibroContable(businessId, comprobanteId, payload) {
    const path = `v1/business/${businessId}/libros-contables/${comprobanteId}`
    return this.http.patch(path, payload)
  }

  //obtener el comprobante de un dato (ingreso / egreso) del libro contable
  getComprobante(params) {
    const { businessId, comprobanteId, type } = params
    const path = `v1/business/${businessId}/libro-comprobante/${comprobanteId}?type=${type}`
    return this.http.get(path)
  }

  //obtener todas cuentas de un (ingreso / egreso) del libro contable
  getAllCuentas() {
    const path = 'v1/cuenta-contable'
    return this.http.get(path)
  }

  patchEditBusiness(businessId, payload) {
    const path = `v1/business/${businessId}`
    return this.http.patch(path, payload)
  }

  getUpdatePreguntonSingleBusiness(businessId) {
    const path = `v1/business/${businessId}/update`
    return this.http.get(path)
  }

  getNotification(params) {
    const { businessId } = params
    const path = `v1/business/${businessId}/notification`
    return this.http.get(path)
  }

  postComprobante(payload: CreateComprobantesDto, businessId) {
    const path = `v1/business/${businessId}/libros-contables`
    return this.http.post(path, payload)
  }

  getLibroComprobantes(
    businessId: number,
    queryFrom: string,
    queryTo: string,
    filter: string,
    limit: number,
    page: number,
    accountId?: number,
    status?: string,
    onlyCreated?: boolean
  ) {
    if (['PAGADO', 'COBRADO', 'ANUL'].includes(status || '')) {
      return { data: { libros: [], count: 0 } }
    }
    // Validación para el parámetro filter
    const validFilters = ['todos', 'aprobados', 'borradores']
    if (!validFilters.includes(filter)) {
      throw new Error(`Filter value is invalid. Expected one of: ${validFilters.join(', ')}`)
    }
    // Codificación de los parámetros para asegurar la URL
    const encodedQueryFrom = encodeURIComponent(queryFrom)
    const encodedQueryTo = encodeURIComponent(queryTo)
    const encodedFilter = encodeURIComponent(filter)

    const path = `/v1/business/${businessId}/libro-comprobantes`

    const queryParams = {
      queryFrom: encodedQueryFrom,
      queryTo: encodedQueryTo,
      filter: encodedFilter,
      limit,
      page,
      status,
      accountId,
      onlyCreated
    }

    return this.http.get(path, { params: queryParams })
  }

  getListRutName(businessId: number) {
    const path = `v1/business/${businessId}/list-rut-name`
    return this.http.get(path)
  }

  getListManual(businessId) {
    const path = `v1/business/${businessId}/list-manual-name`
    return this.http.get(path)
  }

  updateLibroContable(businessId: number, idLibro: number, payload: UpdateLibroContableDto) {
    const path = `v1/business/${businessId}/libros-contables/${idLibro}`
    return this.http.patch(path, payload)
  }

  // Obtener datos del borrador
  findBorrador(businessId: number, id: number) {
    const path = `v1/business/${businessId}/borrador/${id}`
    return this.http.get(path)
  }

  getComprobanteEdit(params) {
    const { businessId, comprobanteId } = params
    const path = `v1/business/${businessId}/libro-comprobante/${comprobanteId}?formato-borrador=${true}`
    return this.http.get(path)
  }

  getComprobanteBorrador(params) {
    const { businessId, comprobanteId } = params
    const path = `v1/business/${businessId}/borrador/${comprobanteId}`
    return this.http.get(path)
  }

  patchComprobante(businessId: number, payload: UpdateMultiLibros) {
    const path = `v1/business/${businessId}/libros-contables/complete`
    return this.http.patch(path, payload)
  }

  updateBorrador(businessId: number, id: number, payload: any, model = 'borrador') {
    const path = `v1/business/${businessId}/borrador/${id}?comprobante=${model}`
    return this.http.patch(path, payload)
  }

  updateAprobado(businessId: number, id: number, payload: any, model = 'manual') {
    const path = `v1/business/${businessId}/borrador/${id}?comprobante=${model}`
    return this.http.patch(path, payload)
  }

  updateVinculacion(businessId: number, id: number, payload: any, model = 'model') {
    const path = `v1/business/${businessId}/borrador/${id}?comprobante=${model}`
    return this.http.patch(path, payload)
  }

  getCuentasContables(params) {
    const { businessId, type } = params
    const path = `v1/cuenta-contable/business/${businessId}/cuentas-last-level?type=${type}`
    return this.http.get(path)
  }

  getListaParaVincular(params, requestOptions = {}) {
    const { businessId, idLibro, from, amountFrom, amountTo, to, limit, page = 0 } = params
    const path = `v1/business/${businessId}/lista-para-vincular/${idLibro}?from=${from}&amountFrom=${amountFrom}&amountTo=${amountTo}&to=${to}&limit=${limit}&page=${page}`
    return this.http.get(path, requestOptions)
  }

  postCajaPrincipal(params) {
    const { businessId, idLibro, monto } = params
    const path = `v1/business/${businessId}/pagar-cobrar/${idLibro}`
    if (monto !== undefined) {
      return this.http.post(path, null, { params: { monto } })
    }
    return this.http.post(path)
  }

  postListaVincular(params, payload) {
    const { businessId, idLibro, din = false, ajuste } = params
    const path = `v1/business/${businessId}/lista-vincular-lista-elegida/${idLibro}${
      din ? '?din=true' : ''
    }`
    return ajuste == null
      ? this.http.post(path, payload)
      : this.http.post(path, payload, { params: { ajuste } })
  }

  postDesvincular(params, payload) {
    const { businessId, idLibro, din = false } = params
    const path = `v1/business/${businessId}/lista-vincular-lista-elegida/${idLibro}?desvincular=${true}${
      din ? '&din=true' : ''
    }`
    const rawIds: unknown[] = Array.isArray(payload?.ids) ? payload.ids : []
    const normalizedIds = Array.from(
      new Set<number>(
        rawIds
          .map(value => Number(value))
          .filter((id): id is number => Number.isFinite(id))
      )
    ).sort((a, b) => a - b)
    if (normalizedIds.length === 0) {
      return Promise.resolve({ data: null })
    }
    const requestKey = `${businessId}:${idLibro}:${normalizedIds.join(',')}`
    const pendingRequest = pendingUnlinkRequests.get(requestKey)
    if (pendingRequest) return pendingRequest

    const request = this.http.post(path, { ...payload, ids: normalizedIds })
    pendingUnlinkRequests.set(requestKey, request)

    const releaseRequest = () => {
      setTimeout(() => {
        if (pendingUnlinkRequests.get(requestKey) === request) {
          pendingUnlinkRequests.delete(requestKey)
        }
      }, 1500)
    }
    request.then(releaseRequest, releaseRequest)

    return request
  }

  postCajaMultiple(params, payload) {
    const { businessId } = params
    const path = `v1/business/${businessId}/pagar-cobrar-multiple`
    return this.http.post(path, payload)
  }

  deleteRemuneracion(businessId, comprobanteId, type) {
    const path = `v1/business/${businessId}/libros-contables/${comprobanteId}?type=${type}`
    return this.http.delete(path)
  }

  getGestion(params: {
    businessId: number | string
    idEmpresa: number | string
    type?: 'compra' | 'venta' | 'honorario'
    year?: number
  }) {
    const { businessId, idEmpresa, type, year } = params

    let path = `v1/business/${businessId}/summary-client/${idEmpresa}`
    const query: string[] = []

    if (type) query.push(`type=${type}`)
    if (year) query.push(`year=${year}`)

    if (query.length) path += `?${query.join('&')}`

    return this.http.get(path)
  }

  postCajaMultipleDes(params, payload) {
    const { businessId } = params
    const path = `v1/business/${businessId}/pagar-cobrar-multiple?desvincular=${true}`
    return this.http.post(path, payload)
  }

  patchAnular(businessId, payload) {
    const path = `v1/business/${businessId}/anular-movimientos`
    return this.http.patch(path, payload)
  }

  patchRatificar(businessId, payload) {
    const path = `v1/business/${businessId}/ratificar-movimientos`
    return this.http.patch(path, payload)
  }

  postResetear(businessId, payload) {
    const path = `v1/business/${businessId}/resetear-conciliacion`
    return this.http.post(path, payload)
  }

  postCajaGestion(params) {
    const { businessId, idLibro, year } = params
    const path = `v1/business/${businessId}/pagar-cobrar/${idLibro}?year=${year}`
    return this.http.post(path)
  }

  patchEmpresa(businessId, idEmpresa, payload) {
    const path = `v1/business/${businessId}/empresa/${idEmpresa}`
    return this.http.patch(path, payload)
  }

  patchEmpresaSiiDiasVencimiento(businessId, empresaSiiId, payload) {
    const path = `v1/business/${businessId}/empresa-sii/${empresaSiiId}/dias-vencimiento`
    return this.http.patch(path, payload)
  }

  patchEmisorDiasVencimiento(businessId, emisorId, payload) {
    const path = `v1/business/${businessId}/emisor/${emisorId}/dias-vencimiento`
    return this.http.patch(path, payload)
  }

  getEmpresa(params) {
    const { businessId, idEmpresa, type } = params
    let path = `v1/business/${businessId}/empresa/${idEmpresa}`
    if (type) {
      path += `?type=${type}`
    }
    return this.http.get(path)
  }

  postBoleta(params, payload, desvincular = false) {
    const { businessId, idLibro } = params
    let path = `v1/business/${businessId}/vincular-lista-boleta-elegida/${idLibro}`
    if (desvincular) {
      path += '?desvincular=true'
    }
    return this.http.post(path, payload)
  }

  postForInOne(params, payload) {
    const { businessId, idLibro } = params
    const path = `v1/business/${businessId}/four-in-one/${idLibro}`
    return this.http.post(path, payload)
  }

  async getTokensAvailable(): Promise<ITokensAvailable> {
    const path = 'v1/organizations/tokens/available'
    const response = await this.http.get<ITokensAvailable>(path)
    return response.data
  }

  getMyBusinesses() {
    const path = 'v1/organizations/my-businesses'
    return this.http.get<IBusiness[]>(path)
  }

  async changeMyCurrentBusiness(organizationId: number): Promise<{ token: string }> {
    const path = `v1/organizations/my-businesses/${organizationId}`
    const response = await this.http.post<{ token: string }>(path)
    return response.data
  }
  getRazonSocial(businessId) {
    const path = `v1/business/${businessId}/list-rut-name`
    return this.http.get(path)
  }

  patchRazonSocial(businessId, id, payload) {
    const path = `v1/business/${businessId}/razon-social/${id}`
    return this.http.patch(path, payload)
  }

  getMasivaCaja(businessId,idProducto,month,year,limit) {
    const today = new Date().toISOString().split('T')[0]
    const now = `${today}T00:00:00Z`
    const path = `v1/business/${businessId}/producto-bancario/${idProducto}/summary-movements?month=${month}&year=${year}&masivo=${true}&limit=${limit}&page=${0}&now=${encodeURIComponent(now)}`
    return this.http.get(path)
  }

}

export default new OrganizationAPI()
